import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import useMessageRefresh from "../../hooks/useMessageRefresh";
import PupilAvailability from "../learner/PupilAvailability";
const API = process.env.REACT_APP_BACKEND_URL || "";

// Plain in-app messaging is included; no AI, automation or paid-channel call.
export default function InstructorConversation({ pupilId, pupilName }) {
  const [threadId, setThreadId] = useState(null);
  const [opened, setOpened] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");
  const [truncated, setTruncated] = useState(false);
  const active = useRef(false);
  const lock = useRef(false);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const accept = useCallback(data => {
    if (data?.thread?.id !== threadId || !Array.isArray(data.messages)) throw new Error("Conversation mismatch");
    setMessages(data.messages); setTruncated(Boolean(data.history_truncated));
  }, [threadId]);
  const { loading, error, load } = useMessageRefresh(`${API}/api/threads/${encodeURIComponent(threadId || "")}/messages`, accept, opened && Boolean(threadId));
  const open = async () => {
    if (lock.current) return;
    if (threadId) { setOpened(true); return; }
    lock.current = true; setBusy(true); setActionError("");
    try {
      const { data } = await axios.post(`${API}/api/threads/for-pupil/${encodeURIComponent(pupilId)}`, {}, { withCredentials: true });
      if (!active.current) return;
      if (!data?.id) throw new Error("Conversation unconfirmed");
      setThreadId(data.id); setOpened(true);
    } catch {
      if (active.current) setActionError("The conversation could not open. You can try again.");
    } finally {
      lock.current = false; if (active.current) setBusy(false);
    }
  };
  const send = async () => {
    if (lock.current || !threadId || !input.trim()) return;
    const words = input.trim(); lock.current = true; setBusy(true); setActionError("");
    try {
      const { data } = await axios.post(`${API}/api/threads/${encodeURIComponent(threadId)}/messages`, { body: words, channel: "in_app" }, { withCredentials: true });
      if (!active.current) return;
      if (!data?.id || data.thread_id !== threadId || data.body !== words || data.direction !== "outbound" || data.channel !== "in_app" || !data.sent_at) throw new Error("Unconfirmed send");
      setMessages(current => current.some(m => m.id === data.id) ? current : [...current, data]);
      setInput(""); await load();
    } catch {
      if (active.current) setActionError("Your message could not be confirmed. Your words are still here. Refresh the conversation before trying again, in case it arrived.");
    } finally {
      lock.current = false; if (active.current) setBusy(false);
    }
  };
  return <section data-testid="instructor-conversation" className="border-t border-slate-200 dark:border-slate-700 pt-3">
    <button type="button" disabled={busy} onClick={() => opened ? setOpened(false) : open()} aria-expanded={opened} className="min-h-11 font-semibold text-blue-700">{opened ? "Close messages" : `Messages with ${pupilName || "pupil"}`}</button>
    {actionError && <p role="alert">{actionError}</p>}
    {opened && <div className="space-y-3">
      <PupilAvailability key={threadId} threadId={threadId} readOnly />
      {loading && <p>Loading messages…</p>}
      {error && <p role="alert">{error}</p>}
      <button type="button" onClick={load} className="min-h-11 underline">Refresh conversation</button>
      {truncated && <p>Showing the most recent messages. Earlier messages are retained.</p>}
      {!loading && !error && !messages.length && <p>No messages yet. You can write below.</p>}
      <ol className="max-h-80 overflow-y-auto space-y-3" aria-label="Conversation messages">{messages.map(message => <li key={message.id}>
        <p className="font-semibold">{message.author_type === "karen" ? "Karen" : message.direction === "inbound" ? pupilName || "Pupil" : "You"}</p>
        <p className="whitespace-pre-wrap">{message.body}</p>
        <p className="text-xs text-slate-500">{message.sent_at ? new Date(message.sent_at).toLocaleString("en-GB") : "Sending status unconfirmed"}</p>
      </li>)}</ol>
      <label className="block">Your message<textarea aria-label="Message to pupil" value={input} disabled={busy || loading} onChange={e => setInput(e.target.value)} rows={3} className="block w-full border rounded px-3 py-2 text-base" /></label>
      <button type="button" onClick={send} disabled={busy || loading || !input.trim()} className="min-h-11 font-semibold text-blue-700">{busy ? "Sending…" : "Send message"}</button>
      <p className="text-xs text-slate-500">Sent inside NDG. Agreeing a time here does not create or change a booking.</p>
    </div>}
  </section>;
}
